// Карта «Рядом с домом» для родителей (/tutors, задача 8).
//
// Читаем ТОЛЬКО представление map_partners_public: опубликованные анкеты,
// без контактов, точка только у building. Телефон сервер отдаёт по клику на
// «Позвонить» / «WhatsApp» — в списке и в представлении его нет.
// Заявка «Не нашли рядом?» — строка в map_parent_requests (service role)
// плюс письмо на support@yolutap.com, как и раньше.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { DISTRICTS, GRADES, isDistrict, isGrade, isSubjectOf, labelOf } from "@/data/map-taxonomy";
import { DISTRICT_LABELS } from "@/lib/map-places";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { sendResendEmail } from "@/lib/email/resend-client.server";
import { CONTACT_KINDS, CONTACT_SOURCES, type ContactKind, type ContactSource } from "@/lib/map-contact-click";
import { clickIsSelf } from "@/lib/contact-self.server";
import { isMissingColumn } from "@/lib/map-partner-notify";
import { BAKU_CITY, cityLabel, cityNameFor, sortCities, type CityName, type MapCity } from "@/lib/map-cities";
import { loadMapCities } from "@/lib/map-cities.server";
import { publicPhotoUrl } from "@/lib/map-photo";
import { idRangeForSuffix, resolveMovedSlug, slugSuffix } from "@/lib/partner-slug";
import { assertTurnstileOk, verifyTurnstile } from "@/lib/security/turnstile.server";
import { utmColumns } from "@/lib/utm";

export type PublicPartner = {
  id: string;
  kind: "tutor" | "club";
  name: string;
  slug: string;
  subjects: string[];
  categories: string[];
  price_azn: number | null;
  price_unit: "lesson" | "month" | "package";
  /** Формат цены (20261027120000): уроков в месяце/пакете, минут, подтверждён ли самим репетитором. undefined — до наката. */
  lessons_per_unit?: number | null;
  lesson_minutes?: number | null;
  price_confirmed?: boolean;
  formats: string[];
  trial_free: boolean;
  grades: string[];
  age_min: number | null;
  age_max: number | null;
  address_mode: "building" | "travel" | "online";
  district: string | null;
  travel_districts: string[];
  metro: string | null;
  about: string | null;
  lang: "az" | "ru" | "en";
  /** Адрес показа (/api/map/photo/<id>), а не путь в бакете; null — фото нет. */
  photo_url: string | null;
  lat: number | null;
  lng: number | null;
  /** Код города (map_cities). undefined/null — до наката 20261025120000: Баку. */
  city?: string | null;
  /** Подписи города из map_cities — их ставит сервер; в представлении их нет. */
  city_name?: CityName | null;
  /** Ориентир (20261031120000) — только у профиля: сервер читает его из map_partners. */
  landmark?: string | null;
  /** Ник Instagram — ссылка на профиле, если ник чистый (src/lib/instagram.ts). */
  instagram?: string | null;
  /** Пауза из кабинета (20261026120000): такой анкеты нет в списке и на карте, по прямой ссылке — с пометкой. */
  is_paused?: boolean;
  paused_until?: string | null;
  /** «Telefon nömrəmi göstər» (20261114120000): false — только WhatsApp, без «Позвонить». undefined — до наката: показан. */
  show_phone?: boolean;
};

const COLS =
  "id, kind, name, slug, subjects, categories, price_azn, price_unit, formats, trial_free, grades, age_min, age_max, address_mode, district, travel_districts, metro, about, lang, photo_url, lat, lng";
/** Колонки миграции 20261025120000. До её наката читаем без них (fail-open по вёрстке). */
const COLS_CITY = `${COLS}, city, instagram`;
/** Колонки миграции 20261026120000 (пауза). До наката — без них, пауза не фильтрует. */
const COLS_PAUSE = `${COLS_CITY}, paused_until, is_paused`;
/** Колонки миграции 20261027120000 (формат цены). До наката — без них: цена без единицы. */
const COLS_PRICE = `${COLS_PAUSE}, lessons_per_unit, lesson_minutes, price_confirmed`;
/** Колонка миграции 20261114120000. До наката — без неё: «Позвонить» у всех. */
const COLS_PHONE = `${COLS_PRICE}, show_phone`;

/* eslint-disable @typescript-eslint/no-explicit-any */
const from = (t: string) => (supabaseAdmin.from as unknown as (t: string) => any)(t);

/**
 * select из представления: сначала с паузой, нет её колонок — с city и
 * instagram, нет и их — без них (с записью в Sentry).
 */
async function readPublic(build: (cols: string) => any): Promise<{ data: any; error: any }> {
  const withPhone = await build(COLS_PHONE);
  if (!withPhone.error || !isMissingColumn(withPhone.error, "show_phone")) return withPhone;
  const withPrice = await build(COLS_PRICE);
  if (!withPrice.error || !(isMissingColumn(withPrice.error, "price_confirmed") || isMissingColumn(withPrice.error, "lessons_per_unit") || isMissingColumn(withPrice.error, "lesson_minutes"))) return withPrice;
  const withPause = await build(COLS_PAUSE);
  if (!withPause.error || !(isMissingColumn(withPause.error, "is_paused") || isMissingColumn(withPause.error, "paused_until"))) return withPause;
  const first = await build(COLS_CITY);
  if (!first.error || !(isMissingColumn(first.error, "city") || isMissingColumn(first.error, "instagram"))) return first;
  try {
    const Sentry = await import("@sentry/cloudflare");
    Sentry.captureMessage("map_partners_public without city/instagram — migration 20261025120000 not applied", "warning");
  } catch {
    /* no-op без DSN */
  }
  return build(COLS);
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * В представлении photo_url — путь в приватном бакете. Наружу уходит только
 * адрес серверного маршрута, который отдаёт файл опубликованной анкеты.
 * Порядок строк и всё остальное от фото не зависят.
 */
const withPhotoUrl = (r: PublicPartner): PublicPartner => ({ ...r, photo_url: r.photo_url ? publicPhotoUrl(r.id, r.photo_url) : null });

/** Подпись города — из справочника map_cities (в представлении только код). */
const withCityName = (cities: readonly MapCity[]) => (r: PublicPartner): PublicPartner => ({ ...r, city_name: cityNameFor(cities, r.city) });

/**
 * Справочник городов для формы join и кабинета: город по пину считается и в
 * браузере (подсказка), и на сервере (запись) — одной функцией cityByPin.
 */
export const listMapCities = createServerFn({ method: "GET" }).handler(async () => ({
  cities: sortCities((await loadMapCities()).filter((c) => c.is_active)),
}));

/** Все опубликованные анкеты. Их немного — фильтры работают в браузере. */
/**
 * Какие страницы предметов есть (≥ 3 анкет) и сколько у них анкет — для
 * внутренних ссылок (главная «Populyar», профессии, профиль; 29.10.2026).
 * Кэш в памяти воркера на 10 минут: страницы профессий и главная не читают
 * базу на каждый показ. Сбой — пусто: ссылок нет, страница работает.
 */
let subjectPagesCache: { at: number; pages: { code: string; n: number }[] } | null = null;
export const getSubjectPages = createServerFn({ method: "GET" }).handler(async () => {
  if (subjectPagesCache && Date.now() - subjectPagesCache.at < 10 * 60_000) return { pages: subjectPagesCache.pages };
  try {
    const { data, error } = await from("map_partners_public").select("kind, subjects, is_paused").limit(2000);
    if (error) throw new Error(error.message);
    const { subjectCounts, subjectPageCodes } = await import("@/lib/tutor-subject-pages");
    const rows = (data ?? []) as { kind: string; subjects: string[] | null; is_paused?: boolean | null }[];
    const counts = subjectCounts(rows);
    const pages = subjectPageCodes(rows).map((code) => ({ code, n: counts.get(code) ?? 0 }));
    subjectPagesCache = { at: Date.now(), pages };
    return { pages };
  } catch (e) {
    console.error("[map-public] subject pages", e);
    return { pages: [] as { code: string; n: number }[] };
  }
});

export const listPublicPartners = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await readPublic((cols) => from("map_partners_public").select(cols).limit(2000));
  // Ошибка чтения — не «пусто»: страница покажет сбой, а не пустую карту.
  if (error) throw new Error(error.message);
  // Справочник городов — для фильтра «Город» и подписей. Сбой чтения — карта
  // без подписей городов и с одним Баку (fail-open по вёрстке, с записью в Sentry).
  let cities: MapCity[] = [];
  try {
    cities = sortCities((await loadMapCities()).filter((c) => c.is_active));
  } catch (e) {
    console.error("[map-public] map_cities", e);
    try {
      const Sentry = await import("@sentry/cloudflare");
      Sentry.captureException(e, { tags: { map_cities: "list" } });
    } catch {
      /* no-op без DSN */
    }
  }
  // На паузе — не в списке и не на карте (прямая ссылка на профиль работает).
  return { rows: ((data ?? []) as PublicPartner[]).filter((r) => !r.is_paused).map(withPhotoUrl).map(withCityName(cities)), cities };
});

/**
 * Анкеты блока «Рекомендуемые» (02.11.2026): is_featured = featured_until > now()
 * и не на паузе. Отдельный запрос из браузера после гидрации, а не поле строк
 * listPublicPartners: строки уходят в HTML хаба и страниц предметов (loader),
 * и их SSR не должен меняться. Наружу — только id, без featured_until.
 * До наката 20261116120000 колонки нет — пусто, блока нет.
 */
export const listFeaturedPartnerIds = createServerFn({ method: "GET" }).handler(async () => {
  const none = { ids: [] as string[] };
  try {
    const { data, error } = await from("map_partners").select("id").eq("status", "published").gt("featured_until", new Date().toISOString()).limit(200);
    if (error) {
      if (!isMissingColumn(error, "featured_until")) console.error("[map-public] featured", error.message);
      return none;
    }
    const ids = ((data ?? []) as { id: string }[]).map((r) => r.id);
    if (!ids.length) return none;
    // Пауза считается в представлении (paused_at, paused_until по Баку) — берём её оттуда.
    const { data: pub, error: e2 } = await from("map_partners_public").select("id, is_paused").in("id", ids);
    if (e2) throw new Error(e2.message);
    return { ids: ((pub ?? []) as { id: string; is_paused?: boolean | null }[]).filter((r) => !r.is_paused).map((r) => r.id) };
  } catch (e) {
    console.error("[map-public] featured", e);
    return none;
  }
});

export const getPublicPartner = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: z.string().min(1).max(80) }).parse(input))
  .handler(async ({ data }) => {
    const { data: row, error } = await readPublic((cols) => from("map_partners_public").select(cols).eq("slug", data.slug).maybeSingle());
    if (error) throw new Error(error.message);
    if (row) {
      const p = withCityName(await loadMapCities())(withPhotoUrl(row as PublicPartner));
      return { row: { ...p, landmark: await landmarkOf(p.id) }, movedTo: null as string | null };
    }
    // Такого slug нет — возможно, анкету переименовали (или slug починили):
    // хвост slug — начало id, по нему находим текущий slug, маршрут ответит 301.
    return { row: null, movedTo: await findMovedSlug(data.slug) };
  });

/**
 * Ориентир опубликованной анкеты — из map_partners (в виде его нет, схему вида
 * 20261031120000 не трогает). До наката или при сбое — null: профиль без ориентира.
 */
async function landmarkOf(id: string): Promise<string | null> {
  const { data, error } = await from("map_partners").select("landmark").eq("id", id).eq("status", "published").maybeSingle();
  if (error) {
    if (!isMissingColumn(error, "landmark")) console.error("[map-public] landmark", error.message);
    return null;
  }
  return typeof data?.landmark === "string" && data.landmark.trim() ? data.landmark.trim() : null;
}

async function findMovedSlug(slug: string): Promise<string | null> {
  const suffix = slugSuffix(slug);
  if (!suffix) return null;
  const { lo, hi } = idRangeForSuffix(suffix);
  const { data, error } = await from("map_partners_public").select("id, slug").gte("id", lo).lte("id", hi).limit(2);
  if (error) throw new Error(error.message);
  return resolveMovedSlug(slug, (data ?? []) as { id: string; slug: string }[]);
}

/** Телефон — только по клику и только у опубликованной анкеты. */
export const getPartnerPhone = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z
      .object({
        id: z.string().uuid(),
        // Что нажали и где — для map_contact_clicks. Старая вкладка их не шлёт: номер отдаётся, клик не пишется.
        kind: z.enum(CONTACT_KINDS).optional(),
        source: z.enum(CONTACT_SOURCES).optional(),
        // С этого устройства входили в кабинет этой анкеты (src/lib/contact-self.ts) — клик самого репетитора.
        self: z.boolean().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const row = await phoneRow(data.id);
    if (!row) throw new Error("not found");
    // «Telefon nömrəmi göstər» выключен (28.10.2026): номер для звонка не отдаём, только WhatsApp.
    const showPhone = row.show_phone !== false;
    if (data.kind === "call" && !showPhone) throw new Error("phone_hidden");
    if (data.kind && data.source) await logContactClick(data.id, data.kind, data.source, await clickIsSelf(data.id, data.self));
    return { phone: showPhone ? String(row.phone) : "", whatsapp: row.whatsapp ? String(row.whatsapp) : String(row.phone) };
  });

/** Телефон, WhatsApp и show_phone. До наката 20261114120000 колонки нет — читаем без неё (номер показан). */
async function phoneRow(id: string): Promise<{ phone: string; whatsapp: string | null; show_phone?: boolean } | null> {
  const q = (cols: string) => from("map_partners").select(cols).eq("id", id).eq("status", "published").maybeSingle();
  let r = await q("phone, whatsapp, show_phone");
  if (r.error && /show_phone/.test(r.error.message ?? "")) r = await q("phone, whatsapp");
  if (r.error) throw new Error(r.error.message);
  return r.data ?? null;
}

/** Клик WhatsApp / «Zəng et». Сбой записи — лог, номер родитель получает всё равно. */
async function logContactClick(partnerId: string, kind: ContactKind, source: ContactSource, isSelf: boolean) {
  try {
    let { error } = await from("map_contact_clicks").insert({ partner_id: partnerId, kind, source, is_self: isSelf });
    // До наката 20261114120000 колонки is_self нет — клик пишется без неё, а не теряется.
    if (error && /is_self/.test(error.message ?? "")) ({ error } = await from("map_contact_clicks").insert({ partner_id: partnerId, kind, source }));
    if (error) console.error("[map-public] contact click insert failed", error.code, error.message);
  } catch (e) {
    console.error("[map-public] contact click insert threw", e);
  }
}

/** Экспорт — для scripts/verify-map-homework.mts. */
export const RequestInput = z.object({
  layer: z.enum(["tutor", "club"]),
  subject: z.string().trim().max(120),
  grade: z.string().refine((g) => g === "" || isGrade(g)),
  age: z.number().int().min(1).max(99).nullable(),
  district: z.string().refine((d) => d === "" || isDistrict(d)),
  // Город (map_cities). Нет поля — заявка из старой вкладки: Баку, как и было.
  city: z.string().regex(/^[a-z_]{2,40}$/).optional(),
  budget: z.number().min(0).max(100000).nullable(),
  phone: z
    .string()
    .trim()
    .min(7)
    .max(40)
    .regex(/^[\d+()\-\s]+$/),
  lang: z.enum(["az", "ru", "en"]),
  turnstile_token: z.string().min(1).max(2048),
  // ── Подбор (20261103120000). Всё необязательно: старая вкладка шлёт без этих полей. ──
  /** Код предмета из справочника; subject выше — подпись, как раньше. */
  subject_code: z.string().max(40).optional(),
  email: z.union([z.literal(""), z.string().trim().toLowerCase().email().max(200)]).optional(),
  /** Согласие получить подборку на почту. Без него письма родителю нет. */
  consent: z.boolean().optional(),
  format: z.enum(["any", "tutor", "home", "online"]).optional(),
  online_ok: z.boolean().optional(),
  teach_lang: z.enum(["az", "ru", "en"]).optional(),
  budget_unit: z.enum(["lesson", "month"]).optional(),
  comment: z.string().trim().max(500).optional(),
  /** ?src= или utm_source страницы. */
  source: z.string().regex(/^[a-z0-9_-]{1,40}$/i).optional(),
  // utm с первого захода на сайт (src/lib/utm.ts), как у анкеты репетитора. Странное значение — не отказ: поле просто пустое.
  utm_source: z.unknown().optional(),
  utm_campaign: z.unknown().optional(),
  /** Ловушка для ботов: поле спрятано, человек его не заполняет. */
  website: z.string().max(200).optional(),
});

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

type RequestData = z.infer<typeof RequestInput>;

/**
 * Город и район заявки: город — только действующая строка map_cities (чужой
 * код → Баку не подставляем, город пустой), район — только у Баку.
 */
export function requestPlace(data: Pick<RequestData, "city" | "district">, cities: readonly MapCity[]): { city: string | null; district: string | null } {
  const raw = data.city ?? BAKU_CITY;
  const city = cities.some((c) => c.code === raw && c.is_active) ? raw : null;
  return { city, district: city === BAKU_CITY && data.district ? data.district : null };
}

/**
 * Строка заявки в map_parent_requests. Пишется ДО письма и поверх него:
 * сбой записи письмо не отменяет (раньше заявка и была только письмом),
 * но и не проходит молча — console.error и событие в Sentry.
 */
async function saveRequestSafely(data: RequestData, place: { city: string | null; district: string | null }): Promise<void> {
  // Именно `.from("…")`, а не помощник from(…) выше: scripts/check-schema-gap.mjs
  // ищет таблицы по `.from("имя")` и иначе не увидел бы эту таблицу в сверке.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const loose = supabaseAdmin as unknown as { from: (t: string) => any };
  try {
    const row = {
      layer: data.layer,
      subject: data.subject || null,
      grade: data.layer === "tutor" && data.grade ? data.grade : null,
      age: data.layer === "club" ? data.age : null,
      city: place.city,
      district: place.district,
      budget_azn: data.budget,
      phone: data.phone,
      lang: data.lang,
    };
    let { error } = await loose.from("map_parent_requests").insert(row);
    // До наката 20261029120000 колонки city нет: заявку не теряем, город — в письме.
    if (error && isMissingColumn(error, "city")) {
      const { city: _city, ...withoutCity } = row;
      ({ error } = await loose.from("map_parent_requests").insert(withoutCity));
    }
    if (error) throw new Error(`map_parent_requests insert: ${error.message}`);
  } catch (e) {
    console.error("[map-request] заявка не записана в базу, уходит только письмом", e);
    try {
      // Лениво: этот файл импортирует и страница, SDK воркера ей не нужен.
      const Sentry = await import("@sentry/cloudflare");
      Sentry.captureException(e, {
        tags: { map_request_db: "insert" },
        extra: { layer: data.layer, lang: data.lang },
      });
    } catch {
      /* no-op без DSN */
    }
  }
}

export type MapRequestResult = {
  ok: true;
  /** new, matched, sent, no_match, closed (повтор), spam; legacy — до наката 20261103120000. */
  status: string;
  /** Подборка ушла на почту родителя. */
  emailed: boolean;
  /** Повтор заявки за сутки — показана подборка первой. */
  duplicate: boolean;
  /** Те же анкеты, что в письме, — карточками на странице. */
  partners: PublicPartner[];
  /** Анкеты подборки, у которых уроки не на языке заявки, — их языки уроков (пометка «ведёт уроки на …»). */
  otherLangs: Record<string, ("az" | "ru" | "en")[]>;
};

/** Анкеты подборки из map_partners_public в порядке подборки (снятые за это время — пропускаются). */
async function publicByIds(ids: string[]): Promise<PublicPartner[]> {
  if (!ids.length) return [];
  const { data, error } = await readPublic((cols) => from("map_partners_public").select(cols).in("id", ids));
  if (error) throw new Error(error.message);
  const cities = await loadMapCities().catch(() => [] as MapCity[]);
  const byId = new Map(((data ?? []) as PublicPartner[]).map((r) => [r.id, r]));
  return ids.map((id) => byId.get(id)).filter((r): r is PublicPartner => !!r && !r.is_paused).map(withPhotoUrl).map(withCityName(cities));
}

/**
 * «Не нашли рядом? Оставьте заявку» → строка в базе, подбор репетиторов,
 * письмо родителю с подборкой и копия админу (map-match.server.ts).
 * До наката 20261103120000 — по-старому: строка и письмо админу.
 */
export const submitMapRequest = createServerFn({ method: "POST" })
  .inputValidator((input) => RequestInput.parse(input))
  .handler(async ({ data }): Promise<MapRequestResult> => {
    const verdict = await verifyTurnstile(data.turnstile_token);
    assertTurnstileOk(verdict);
    // Справочник не прочитался — город заявки пустой, заявка всё равно уходит.
    const cities = await loadMapCities().catch((e) => {
      console.error("[map-request] map_cities", e);
      return [] as MapCity[];
    });
    const place = requestPlace(data, cities);
    const subjectCode = data.subject_code && isSubjectOf(data.layer, data.subject_code) ? data.subject_code : "";
    const m = await import("@/lib/map-match.server");
    try {
      const r = await m.processParentRequest({
        layer: data.layer,
        subject: data.subject,
        subject_code: subjectCode,
        grade: data.layer === "tutor" && data.grade ? data.grade : null,
        age: data.layer === "club" ? data.age : null,
        city: place.city,
        district: place.district,
        format: data.format ?? "any",
        online_ok: data.online_ok ?? false,
        teach_lang: data.teach_lang ?? data.lang,
        budget_azn: data.budget,
        budget_unit: data.budget_unit ?? "lesson",
        comment: data.comment || null,
        phone: data.phone,
        email: data.email || null,
        consent: data.consent ?? false,
        lang: data.lang,
        source: data.source ?? null,
        ...utmColumns(data),
        honeypot: data.website ?? "",
      });
      // Карточки — поверх письма: их сбой подборку не отменяет, страница скажет «отправили».
      const partners = await publicByIds(r.partnerIds).catch((e) => {
        console.error("[map-request] public cards", e);
        return [] as PublicPartner[];
      });
      const otherLangs = data.layer === "tutor" ? await m.otherLangsForIds(data.teach_lang ?? data.lang, partners.map((p) => p.id)) : {};
      return { ok: true, status: r.status, emailed: r.emailed, duplicate: r.duplicate, partners, otherLangs };
    } catch (e) {
      if (e instanceof m.RateLimited) throw new Error("rate_limited");
      if (!(e instanceof m.MatchSchemaMissing)) throw e;
      try {
        const Sentry = await import("@sentry/cloudflare");
        Sentry.captureMessage("map_parent_requests without match columns — migration 20261103120000 not applied", "warning");
      } catch {
        /* no-op без DSN */
      }
    }
    // ── По-старому (до наката 20261103120000) ──
    // Запись — первой: даже если ниже не окажется ключа Resend или письмо
    // упадёт, заявка уже лежит в базе.
    await saveRequestSafely(data, place);
    const apiKey = process.env.RESEND_API_KEY;
    if (!apiKey) throw new Error("RESEND_API_KEY missing");
    const lines: [string, string][] = [
      ["Кого ищут", data.layer === "tutor" ? "репетитора" : "кружок"],
      ["Предмет / направление", data.subject || "—"],
      [data.layer === "tutor" ? "Класс" : "Возраст", data.layer === "tutor" ? (data.grade ? labelOf(GRADES, data.grade, "ru") : "—") : data.age === null ? "—" : String(data.age)],
      ["Город", place.city ? cityLabel(cities, place.city, "ru") : data.city ?? "—"],
      ["Район", place.district ? labelOf(DISTRICT_LABELS, place.district, "ru") : "—"],
      ["Бюджет, ₼", data.budget === null ? "—" : String(data.budget)],
      ["Телефон", data.phone],
      ["Язык", data.lang.toUpperCase()],
    ];
    // Ошибка отправки по-прежнему уходит родителю, а не глотается: если
    // строка в базу не записалась (см. выше), письмо — единственная копия
    // заявки, и родитель должен знать, что её стоит отправить ещё раз.
    await sendResendEmail(
      {
        from: "Yolutap <notify@notify.yolutap.com>",
        to: "support@yolutap.com",
        subject: `Заявка с карты: ${data.subject || (data.layer === "tutor" ? "репетитор" : "кружок")} — ${place.district ? labelOf(DISTRICT_LABELS, place.district, "ru") : place.city && place.city !== BAKU_CITY ? cityLabel(cities, place.city, "ru") : "район не указан"}`,
        html: `<table cellpadding="6">${lines.map(([k, v]) => `<tr><td><b>${esc(k)}</b></td><td>${esc(v)}</td></tr>`).join("")}</table>`,
        text: lines.map(([k, v]) => `${k}: ${v}`).join("\n"),
        tags: [{ name: "kind", value: "map_request" }],
      },
      { apiKey },
    );
    return { ok: true, status: "legacy", emailed: false, duplicate: false, partners: [], otherLangs: {} };
  });
